import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { getDateWithoutTimezoneShift } from '@lender/utils';
import { useTheme } from '@mui/material/styles';
import { uncompletedLoanErrorMessageParams } from 'app/pages/loanApplication/LoanApplication.constants';
import { getSharedAccordionStyles } from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import LoanTabLayout from 'app/pages/loanApplication/LoanTabLayout';
import PrimaryDataWrapper from 'app/shared/components/loanManagement/PrimaryDataWrapper';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';

import { COMMON_DATETIME_FORMAT } from 'constants/date';
import { FeatureFlag } from 'constants/featureFlags';
import { LoanApplicationTabRouteParam, LoanType } from 'constants/loanApplication';
import useLoanId from 'hooks/useLoanId';
import usePendingChangeAuthors from 'hooks/usePendingChangeAuthors';
import { getConditionalObject } from 'utils/data';
import { getTabEntityUiManageId, getViewDataChanges } from 'utils/loanApplication';

import { showMessageAction } from 'store/global/actions';
import {
  showMessageThatCurrentUserCantChangeLoanAction,
  toggleEditStateAction,
} from 'store/loanApplications/actions';
import {
  selectCommonInfoLoanApplication,
  selectEntityChangedPropsById,
  selectIsCurrentUserCanChangeLoan,
  selectIsEditingSection,
  selectIsLoanApplicationEditAllowed,
  selectIsLoanCanceled,
  selectIsLoanInitiatedWithErrors,
  selectUnderwritingExpirationDates,
  selectUnderwritingNotes,
} from 'store/loanApplications/selectors';

import { UnderwritingTestIds } from '../Underwriting.testIds';

import GeneralForm from './GeneralForm';
import { generalViewConfigFields, getExpirationDatesViewConfig } from './GeneralView.config';

const getFormattedExpirationDate = value =>
  value ? dayjs(getDateWithoutTimezoneShift(value)).format(COMMON_DATETIME_FORMAT) : 'N/A';

const General = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();

  const theme = useTheme();
  const sharedClasses = useMemo(() => getSharedAccordionStyles(theme), [theme]);

  const loanId = useLoanId();

  const generalUiManageId = getTabEntityUiManageId({
    tabName: LoanApplicationTabRouteParam.UNDERWRITING,
    id: 'general',
  });

  const isUnderwritingEcdEnabled = useFeatureToggle(FeatureFlag.EnableUnderwritingEcd);

  const isEditMode = useSelector(selectIsEditingSection(generalUiManageId));
  const underwritingExpirationDates = useSelector(selectUnderwritingExpirationDates);
  const underwritingNotes = useSelector(selectUnderwritingNotes);
  const isLoanInitedWithErrors = useSelector(selectIsLoanInitiatedWithErrors);
  const isCurrentUserCanChangeLoan = useSelector(selectIsCurrentUserCanChangeLoan);
  const isLoanCanceled = useSelector(selectIsLoanCanceled);
  const isEditAllowed = useSelector(selectIsLoanApplicationEditAllowed) && !isLoanCanceled;
  const pendingChangesData = useSelector(selectEntityChangedPropsById(loanId));
  const pendingChangeAuthors = usePendingChangeAuthors(loanId);
  const { loanType } = useSelector(selectCommonInfoLoanApplication);

  const isEcdHidden = [undefined, LoanType.HELOC].includes(loanType);

  const onToggleEditMode = useCallback(() => {
    if (isLoanInitedWithErrors) {
      return dispatch(showMessageAction(uncompletedLoanErrorMessageParams));
    }

    if (!isCurrentUserCanChangeLoan) {
      return dispatch(showMessageThatCurrentUserCantChangeLoanAction());
    }

    dispatch(toggleEditStateAction(generalUiManageId));
  }, [dispatch, generalUiManageId, isCurrentUserCanChangeLoan, isLoanInitedWithErrors]);

  const expirationDatesViewConfig = useMemo(
    () =>
      getExpirationDatesViewConfig({
        onEdit: onToggleEditMode,
        isEditAllowed,
        isLoading,
        isEcdHidden,
      }),
    [onToggleEditMode, isEditAllowed, isLoading, isEcdHidden],
  );

  const formattedForViewFormValues = useMemo(() => {
    const { earliestClosingDate, manuallyUnderwritten } = underwritingExpirationDates;

    return {
      manuallyUnderwritten: _.capitalize(String(manuallyUnderwritten ?? false)),
      underwritingManualNotes: underwritingNotes,
      ...getConditionalObject(isUnderwritingEcdEnabled && !isEcdHidden, () => ({
        earliestClosingDate: getFormattedExpirationDate(earliestClosingDate),
      })),
    };
  }, [underwritingExpirationDates, underwritingNotes, isUnderwritingEcdEnabled, isEcdHidden]);

  const generalDataChanges = useMemo(
    () =>
      getViewDataChanges({
        pendingChangesData,
        configFields: generalViewConfigFields,
      }),
    [pendingChangesData],
  );

  return (
    <LoanTabLayout
      content={
        <div className={clsx(sharedClasses.accordion, 'w-full')}>
          {isEditMode && (
            <GeneralForm
              applicationData={underwritingExpirationDates}
              isEcdHidden={isEcdHidden}
              isLoading={isLoading}
              loanId={loanId}
              pendingChangesData={pendingChangesData}
              setIsLoading={setIsLoading}
              onCancelForm={onToggleEditMode}
            />
          )}

          {!isEditMode && (
            <PrimaryDataWrapper
              removeBottomMargin
              config={expirationDatesViewConfig}
              data={formattedForViewFormValues}
              dataAuthors={pendingChangeAuthors}
              dataChanges={generalDataChanges}
              dataTestId={UnderwritingTestIds.GENERAL_FORM_LOAN_EXPIRATION_DATE_DATA_BUTTON}
              isButtonsVisible={isEditAllowed}
            />
          )}
        </div>
      }
      isRowLayout={false}
      useBottomButtons={false}
    />
  );
};

General.propTypes = {
  ...WithLoadingPropTypes,
};

export default memo(General);
